// Klient-side felrapportering. Fire-and-forget: bygger payload i idle-tid och
// POSTar med keepalive så den ALDRIG blockerar rendering eller interaktion.
//
// Prestanda-garantier:
// - Session-dedup: samma fel skickas max en gång per session.
// - Global cap: max 10 distinkta auto-fel per session (skydd mot fel-storm).
// - reportError kastar aldrig (felfångst som kraschar = oändlig loop).

import { getActivity } from "@/lib/activity-log";

export type ClientErrorKalla =
  | "error-boundary"
  | "global-error"
  | "window-onerror"
  | "unhandledrejection";

export interface ClientErrorPayload {
  kalla: ClientErrorKalla;
  meddelande: string;
  stack?: string;
  componentStack?: string;
  digest?: string;
}

const sentFingerprints = new Set<string>();
const MAX_PER_SESSION = 10;

function clientFingerprint(p: ClientErrorPayload, pathname: string): string {
  const firstFrame = (p.stack ?? "").split("\n")[1]?.trim() ?? "";
  return `${p.meddelande.slice(0, 200)}|${firstFrame}|${pathname}`;
}

function whenIdle(fn: () => void): void {
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback;
  if (typeof ric === "function") ric(fn);
  else setTimeout(fn, 0);
}

export function reportError(payload: ClientErrorPayload): void {
  try {
    if (typeof window === "undefined") return;
    const pathname = window.location.pathname;
    const fp = clientFingerprint(payload, pathname);
    if (sentFingerprints.has(fp)) return;
    if (sentFingerprints.size >= MAX_PER_SESSION) return;
    sentFingerprints.add(fp);

    whenIdle(() => {
      try {
        const body = JSON.stringify({
          kalla: payload.kalla,
          meddelande: payload.meddelande,
          stack: payload.stack,
          componentStack: payload.componentStack,
          digest: payload.digest,
          url: pathname,
          userAgent: navigator.userAgent,
          activityLog: getActivity(),
        });
        fetch("/api/error-report", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        }).catch(() => {});
      } catch {
        /* svälj */
      }
    });
  } catch {
    /* svälj — felfångst får aldrig kasta */
  }
}
